import ComparePage from './CompareLayout';
import { CAYDA, ADV } from './caydaFacts';

const VsPastel = () => (
  <ComparePage
    slug="pastel"
    competitor="Pastel"
    description="Pastel alternative: Cayda runs on your real site instead of a proxied canvas, works behind login, and puts Jira and Asana on a $14 tier instead of $99."
    hero="The Pastel alternative that runs on your real site."
    intro="Pastel loads your site through its proxy into a canvas that reviewers open by link. Cayda runs from one script tag on your own domain, so your team reviews the page that actually shipped, logged in as themselves, with every note anchored to the real element. That difference decides what you can review, and what you pay for it."
    verdict={[
      { label: 'Starting price', cayda: '$9/mo', other: '$29/mo' },
      { label: 'Extra teammate', cayda: '$6', other: '$24 on Team' },
      { label: 'Runs on', cayda: 'Your domain', other: 'A proxied canvas' },
      { label: 'Jira + Asana', cayda: '$14 tier', other: '$99 tier' },
    ]}
    advantages={[
      { ...ADV.realSite, body: 'One script tag on your own domain. Pages behind login, dashboards, checkout flows and single page apps work because your team is using the real site with their own session. Nothing passes through a third party proxy, and no password ever leaves your site.' },
      { ...ADV.pricing, body: 'A base fee covers you; each accepted teammate is $5 to $6, not $24. Five people with Jira and Asana pay $38, not $99. Unlimited sites on every paid plan, so there is no canvas count to manage.' },
      { ...ADV.invisible, body: 'The layer exists only for authenticated team members and invited guests, so you can leave notes on production without a staging copy or a review link. Slack on Pro; Jira, Asana and Linear on Team.' },
      { ...ADV.capture, body: 'Every note pins to the element and records a screenshot, page URL, browser, OS and viewport. Screen recording on the Team plan captures the steps, not just the spot.' },
    ]}
    cost={{
      note: 'Annual billing, per month. Pastel pricing from usepastel.com/plans, September 2026.',
      scenarios: [
        { label: 'Solo, several sites', detail: 'unlimited sites on Cayda', cayda: 9, other: 29, caydaPlan: 'Pro, owner covered', otherPlan: 'Pro, 3 canvases' },
        { label: 'Team of five, Jira + Asana', detail: 'plus Linear, screen recording, guests', cayda: CAYDA.teamPrice(4), other: 99, caydaPlan: 'Team + 4 members', otherPlan: 'Team, 5 users' },
        { label: 'Team of eight, Jira + Asana', detail: 'extra users at $6 vs $24', cayda: CAYDA.teamPrice(7), other: 171, caydaPlan: 'Team + 7 members', otherPlan: 'Team + 3 extra users' },
      ],
    }}
    diagram="proxy"
    features={{
      note: 'Cayda plan names in the Cayda column. Pastel details from usepastel.com/plans and its help centre, September 2026.',
      rows: [
        ['Free plan', CAYDA.free, '1 user · 1 active canvas'],
        ['Entry paid plan', CAYDA.pro, '$29 · 2 users · 3 canvases'],
        ['Jira, Asana', CAYDA.team, 'Team ($99)'],
        ['Linear', 'Team', 'Not listed'],
        ['Slack', 'Pro', 'Not listed'],
        ['Extra teammate', '$5 to $6', '$24'],
        ['Runs on your live domain', true, false],
        ['Pages behind login, web apps', true, 'Via proxy'],
        ['Visible to site visitors', 'Never', 'Never (separate link)'],
        ['Auto screenshot, browser, OS, viewport', true, 'Screenshot, browser, screen size'],
        ['Screen recording', 'Team', 'Not listed'],
        ['Kanban + list dashboard, bulk actions', true, 'Comment list'],
        ['Client / guest reviewers', 'Team, no member fee', 'Unlimited, no account'],
        ['CSV export', 'Pro', 'All plans'],
        ['WordPress plugin, Shopify app', true, 'Not listed'],
      ],
    }}
    approach={[
      'Pastel is a client review canvas. You paste a URL, Pastel fetches the site through its proxy and presents it inside a canvas, and reviewers comment on that canvas by link without an account. It also reviews images, PDFs and videos, with approval and deadline features aimed at getting a client to sign off. Its help centre notes that requests, including any passwords entered, pass through its servers, and integrations with Jira, Asana, Trello and ClickUp sit on the $99 Team plan.',
      'Cayda takes the other route: the review happens on your real site, for your real team, with the real page state, and the tracker integrations sit on a $14 base fee.',
    ]}
    bottomLine={[
      'If what you are reviewing is a live application, a page behind login, or a portfolio of client sites, Cayda is the tool that works on the thing itself, and it costs less at every team size. Add a site free and click on it.',
    ]}
  />
);

export default VsPastel;
